import { PageProps, graphql } from "gatsby";
import ThemeHelper from "components/helpers/theme-helper.js";
import {
  BrowserSupportHelper,
  ECommerceHelper,
  LanguageHelper,
  PageProviders,
  SimplifyGatsbyQuery,
  extract,
  getCurrentContentPageSlugsData,
} from "@parksandresorts/core";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import Page from "components/page";
import { SiteInfoHelper } from "@parksandresorts/core";
import { getCalendarLoadersForTheme } from "components/helpers/icon-helper";
import { SearchHelper } from "@parksandresorts/core";
import { StoreItem } from "../components/block-elements/search/lunr-search";
import { useEffect, useState } from "react";

export { Head } from "components/head/content-page-head";

/*There might exist some more things on this object that Gatsby magically puts there.*/
export type ContentfulContentPageData = SimplifyGatsbyQuery<Queries.ContentPageByQuery> & {
  allContentfulContentPage: { nodes: Array<{ node_locale: "en" | "sv"; slug: string }> };
  contentfulContentPage: { node_locale: "en" | "sv" };
};

export type ContentfulContentPageContext = {
  //What is being passed in as context in createPage in create-content-page.js
  slug: string;
  contentful_id: string;
  node_locale: string;
  excludePageFromExternalSearch: boolean | null;
  excludePageFromInternalSearch: boolean | null;
  pageTheme: string | null;
  pageType: "contentPage";
};

const ContentPageTemplate = (props: PageProps<ContentfulContentPageData, ContentfulContentPageContext>) => {
  LanguageHelper.setLocale(props.data.contentfulContentPage.node_locale);

  SiteConfigurationStore.init(
    extract(props, "data.contentfulSiteConfiguration"),
    extract(props, "data.contentfulContentPage.node_locale"),
  );
  ECommerceHelper.initJetshop(extract(props, "data.contentfulEcommerceConfig"));
  ThemeHelper.setTheme(extract(props, "data.contentfulSiteConfiguration.siteTheme", "gronalund"));

  const pageTheme = props.data.contentfulContentPage.pageTheme ?? "page-theme-none";
  const iconSet = getCalendarLoadersForTheme(pageTheme);
  SiteInfoHelper.setCalendarLoaderIcons(iconSet);

  SearchHelper.getSingelton<StoreItem>().setUrls({
    sv: {
      publicIndexURL: props.data.allLocalSearchSvPage?.nodes[0]?.publicIndexURL,
      publicStoreURL: props.data.allLocalSearchSvPage?.nodes[0]?.publicStoreURL,
    },
    en: {
      publicIndexURL: props.data.allLocalSearchEnPage?.nodes[0]?.publicIndexURL,
      publicStoreURL: props.data.allLocalSearchEnPage?.nodes[0]?.publicStoreURL,
    },
    svRestaurant: {
      publicIndexURL: props.data.allLocalSearchSvRestaurant?.nodes[0]?.publicIndexURL,
      publicStoreURL: props.data.allLocalSearchSvRestaurant?.nodes[0]?.publicStoreURL,
    },
    enRestaurant: {
      publicIndexURL: props.data.allLocalSearchEnRestaurant?.nodes[0]?.publicIndexURL,
      publicStoreURL: props.data.allLocalSearchEnRestaurant?.nodes[0]?.publicStoreURL,
    },
  });

  /**
   * We tried handling this in the Head-components (e.g., content-page-head.tsx),
   * but we need to set the viewport meta tag based on the device type.
   * It works in Head when navigating, but fails when a page is loaded directly, so we have to do it here.
   */
  const [deviceType, setDeviceType] = useState<ReturnType<typeof BrowserSupportHelper.getDeviceType> | null>(null);

  useEffect(() => {
    setDeviceType(BrowserSupportHelper.getDeviceType());
  }, []);

  useEffect(() => {
    if (deviceType === null) {
      return;
    }
    const viewPort = document.querySelector(`meta[name=viewport]`);
    viewPort?.setAttribute(
      "content",
      `initial-scale=1, width=device-width, viewport-fit=cover${
        deviceType === "iOS" ? ", user-scalable=0, maximum-scale=1" : ""
      }`,
    );
    viewPort?.setAttribute("data-device-type", deviceType);
  }, [deviceType]);

  return (
    <PageProviders
      currentContentPageData={{
        slugs: getCurrentContentPageSlugsData(props.data.allContentfulContentPage.nodes),
        currentLocale: LanguageHelper.getCurrentLocale(),
      }}
      staticConfigData={{
        eCommerce: {
          shopServicePaymentSlug: props.data.contentfulEcommerceConfig.shopServicePaymentPage.slug,
          shopServiceConfirmationSlug: props.data.contentfulEcommerceConfig.shopServiceConfirmationPage.slug,
          shopServiceCheckoutSlug: props.data.contentfulEcommerceConfig.shopServiceCheckoutPage.slug,
        },
      }}
    >
      <Page {...props} />
    </PageProviders>
  );
};

export default ContentPageTemplate;

export const pageQuery = graphql`
  query ContentPageBy($slug: String!, $node_locale: String!, $contentful_id: String!) {
    contentfulContentPage(slug: { eq: $slug }) {
      contentful_id
      id
      node_locale
      pageTheme
      title
      slug
      description
      keywords
      socialMediaSharedImage {
        file {
          url
        }
      }
      navigation {
        ...MainNavigation
      }
      metaTitle
      excludePageFromExternalSearch
      excludePageFromInternalSearch
      excludePageFromSiteBuild
      lockType
      lockValue
      lockInputLabel
      lockInputPlaceholder
      lockErrorMessage
      dateTimeLock
      hideBroadcastMessage
      enableGoogleOptimizeAntiFlickerScript
      blocks {
        __typename
        ...TextBlock
        ...CardListItemBlock
        ...FactBlock
        ...ListBlock
        ...InformationBlock
        ...ShowBlock
        ...RestaurantBlock
        ...RideBlock
        ...MyPageBlock
        ...GeneralContentBlock
        ...MediaGalleryBlock
        ...HeroBlock
        ...ToolbarBlock
        ...AdmissionBlock
        ...UpcomingBlock
        ...DailyScheduleBlock
        ...ProductListBlock
        ...ProductBlock
        ...BackgroundWrapperBlock
        ...IframeCaspecoBlock
        ...RestaurantMenuBlock
        ...FormBlock
        ...ShowBookingBlock
        ...Link
        ...GroupAdmissionBlock
        ...SeparateJetpassBookingBlock
        ...SeasonPassPurchaseBlock
        ...CountdownBlock
      }
    }
    allContentfulContentPage(filter: { contentful_id: { eq: $contentful_id } }) {
      nodes {
        node_locale
        slug
      }
    }
    contentfulEcommerceConfig(node_locale: { eq: $node_locale }, primary: { eq: true }) {
      ...ECommerceConfig
    }
    contentfulSiteConfiguration(node_locale: { eq: $node_locale }, primary: { eq: true }) {
      ...SiteConfiguration
    }
    allContentfulEntryProductTemplateBlock {
      edges {
        node {
          id
          node_locale
          internalTitle
          templateId
          templateImage {
            file {
              url
            }
          }
          templateDescription {
            templateDescription
          }
        }
      }
    }
    allLocalSearchSvRestaurant {
      nodes {
        publicIndexURL
        publicStoreURL
      }
    }
    allLocalSearchEnRestaurant {
      nodes {
        publicIndexURL
        publicStoreURL
      }
    }
    allLocalSearchSvPage {
      nodes {
        publicIndexURL
        publicStoreURL
      }
    }
    allLocalSearchEnPage {
      nodes {
        publicIndexURL
        publicStoreURL
      }
    }
  }
`;
